import React from "react";
import _ from "lodash";
import moment from "moment";
import cx from "classnames";
import submit from "submit-form";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import getDayRangeString from "../../../utils/day-range-string";
import CopyExerciseModal from "../../../parts/modals/copy-exercise";
import ExportWorkoutModal from "../../../parts/modals/export-workout";
import ShareModal from "../../../parts/modals/share";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";

// Constants
const {
  ZONES,
  PRIMARIES,
  WEIGHT_UNITS,
  DATE_FORMATS: { DATE_DAY_FORMAT },
} = require("../../../constants");

const ZONES_LEGEND = _.map(ZONES, (name) => {
  return (
    <div key={name} className={`bar series ${name}`}>
      {_.capitalize(name)}
    </div>
  );
});

const PRIMARIES_LEGEND = _.map(PRIMARIES, (name) => {
  return (
    <div key={name} className={`bar series ${name}`}>
      {_.capitalize(name)}
    </div>
  );
});

class View extends React.Component {
  initDND() {
    if (!this.refs.exercises) return;
    const dragula = require("react-dragula");
    this.exercises = dragula([this.refs.exercises], {
      invalid: (el, handle) =>
        !_.includes(handle.getAttribute("class"), "sort-handle"),
    }).on("drop", handleDrop);

    // Store original reactids so that we can ensure they are consistent.
    const originalIDs = _.map(_.get(this.refs, "exercises.children"), (el) =>
      el.getAttribute("data-reactid")
    );
    function handleDrop(el, target) {
      // Submit a form to update the order in the backend.
      submit("/actions/exercise/order", {
        method: "POST",
        body: {
          exercises: _.map(target.children, (el) => el.getAttribute("data-id")),
        },
      });
    }
  }
  stopDND() {
    this.exercises && this.exercises.destroy();
  }

  componentDidMount() {
    this.initDND();
  }
  componentDidUpdate() {
    this.stopDND();
    this.initDND();
  }
  componentWillUnmount() {
    this.stopDND();
  }

  render() {
    const { locals } = this.context;
    const {
      query,
      activeExercises,
      activeUser,
      exercisesVolume,
      exercisesLifts,
      exercisesVolumeGroupZones,
    } = locals;
    const { units } = query;

    const activeDay = moment(query.day, DATE_DAY_FORMAT);
    const selectDayRange = _.map(_.range(0, 7), (i) =>
      activeDay.clone().add(i, "days")
    );
    const metricDayRange =
      query.period === "day" ? [activeDay, activeDay] : selectDayRange;
    const totalLifts = _.sum(_.values(exercisesLifts));
    const averageItensity = 0;

    const seriesVolume = _(PRIMARIES)
      .map((name) => {
        return {
          name,
          className: name,
          data: [
            locals.fromMetric(_.get(exercisesVolume, [name, "volume"]), units),
          ],
        };
      })
      .value();

    const seriesVolumeSplit = _(PRIMARIES)
      .map((name) => {
        return {
          name,
          className: name,
          value: locals.fromMetric(
            _.get(exercisesVolume, [name, "volume"]),
            units
          ),
        };
      })
      .filter("value")
      .value();

    //TODO; come back and fix this to remove the duplication
    const seriesVolumeIntensitySplit = _(PRIMARIES)
      .map((name) => {
        return {
          name,
          className: name,
          value: _.get(exercisesVolume, [name, "volume"]),
        };
      })
      .filter("value")
      .value();

    const totalVolume = _.sumBy(seriesVolumeSplit, "value");
    const totalIntensityVolume = _.sumBy(seriesVolumeIntensitySplit, "value");
    const totalIntensity = _.reduce(
      exercisesVolume,
      (prev, metric) =>
        prev + metric.average * (metric.volume / totalIntensityVolume),
      0
    );
    const selectedZonesMetrics =
      query.zoneType === "total"
        ? exercisesVolumeGroupZones
        : _.pick(exercisesVolumeGroupZones, query.zoneType);
    const totalZoneVolume = locals.fromMetric(
      _(selectedZonesMetrics).flatMap(_.values).sum(),
      units
    );
    const seriesZones = _(ZONES)
      .map((zone) => {
        return {
          name: zone,
          className: zone,
          value:
            locals.fromMetric(
              _.sumBy(_.values(selectedZonesMetrics), zone) || 0,
              units
            ) / totalZoneVolume,
        };
      })
      .filter("value")
      .value();

    if (_.isEmpty(seriesZones)) {
      seriesZones.push({
        className: "empty",
        name: "empty",
        value: 1,
      });
    }

    return (
      <form id="program-builder-form" className="Program-Builder" action="#_">
        <div className="Sub-Header">
          <div className="Day-Slider">
            <h1 className="alt">{getDayRangeString(selectDayRange)}</h1>
            <a
              href="/dashboard/program-builder/calendar"
              className="text-button icon"
            >
              <img src="/img/svg/icon_calendar.svg" alt="icon" />
              My Calendar
            </a>

            <div className="days">
              <a
                className="prev double"
                href={`/actions/refresh?day=${activeDay
                  .clone()
                  .add(-7, "days")
                  .format(DATE_DAY_FORMAT)}`}
              />
              <a
                className="prev"
                href={`/actions/refresh?day=${activeDay
                  .clone()
                  .add(-1, "days")
                  .format(DATE_DAY_FORMAT)}`}
              />

              {_.map(selectDayRange, (day) => {
                const fullday = day.format(DATE_DAY_FORMAT);
                const active = fullday === _.get(query, "day");
                return (
                  <a
                    key={fullday}
                    className={cx("day", { active })}
                    href={`/actions/refresh?day=${fullday}`}
                  >
                    {day.format("ddd")}
                    <br />
                    {day.format("D")}
                  </a>
                );
              })}

              <a
                className="next"
                href={`/actions/refresh?day=${activeDay
                  .clone()
                  .add(1, "days")
                  .format(DATE_DAY_FORMAT)}`}
              />
              <a
                className="next double"
                href={`/actions/refresh?day=${activeDay
                  .clone()
                  .add(7, "days")
                  .format(DATE_DAY_FORMAT)}`}
              />
            </div>

            <div className="picker">
              <DateInput
                name="day"
                placeholder="Date"
                value={activeDay}
                onChange={handleDayChange}
              />
            </div>
          </div>
        </div>

        <div className="Main">
          <div className="section">
            <h4 className="alt">
              Today's Workout &nbsp;{" "}
              <span className="thin">
                {activeDay.format("dddd, D MMMM YYYY")}
              </span>
            </h4>
            <div className="exercise-buttons">
              <a
                className="button"
                href={`/dashboard/program-builder/edit-exercise/?day=${activeDay.format(
                  DATE_DAY_FORMAT
                )}`}
              >
                Add an Exercise
              </a>
              <a
                className="button outline"
                href={`/actions/refresh?modal[type]=copy&modal[date]=${activeDay.format(
                  "YYYY-MM"
                )}`}
              >
                Import A Workout
              </a>
              <a
                className="button outline"
                href={`/actions/refresh?modal[type]=export&modal[date]=${activeDay.format(
                  DATE_DAY_FORMAT
                )}`}
              >
                Export Today's Workout
              </a>
            </div>

            <div className="Workout">
              <div className="head">
                <b>Exercises</b>
              </div>

              <div
                className="details"
                ref="exercises"
                key={_(activeExercises).map("_id").join()}
              >
                {_.map(activeExercises, (exercise, i) => {
                  // This is setup this way as assistant used to be a primary exercise type but now
                  // primary exercies are caleld types (primary/secondary/assistant) but it would have been days
                  // of effort to fix this so we do a bit of hackery to set it correctly if the type is assistant.

                  const isAssistant =
                    _.get(exercise, "type", false) === "assistant";
                  const primary = !isAssistant
                    ? _.get(exercise, "primary")
                    : "Assistant";
                  const defaultRepMax = locals.fromMetric(
                    _.get(
                      exercise,
                      `repMaximums.${primary}`,
                      _.get(activeUser, `repMaximums.${primary}`, 0)
                    )
                  );
                  const equipped =
                    !isAssistant && _.some(exercise.sets, "equipped");
                  const collapsible = !_.isEmpty(exercise.sets);
                  const sortable = _.size(activeExercises) > 1;
                  const classname = !isAssistant ? primary : "assistant";

                  return (
                    <div
                      className={cx("Exercise editable", classname, {
                        equipped,
                        sortable,
                        collapsible,
                      })}
                      key={exercise._id}
                      data-id={exercise._id}
                    >
                      <input
                        key={query.expand}
                        type="checkbox"
                        id={`exercise-toggle-${exercise._id}`}
                        defaultChecked={query.expand}
                      />

                      <div className="sort-handle">
                        <img
                          className="sort-handle"
                          src="/img/svg/icon_drag.svg"
                          alt="sort handle"
                        />
                      </div>

                      <div className="head">
                        <label
                          className="expand-toggle"
                          htmlFor={`exercise-toggle-${exercise._id}`}
                        />
                        <a
                          className="edit"
                          href={`/dashboard/program-builder/edit-exercise/${exercise._id}`}
                        >
                          Edit
                        </a>
                        {_.capitalize(primary)}{" "}
                        {exercise.secondary &&
                          `(${_.capitalize(exercise.secondary)})`}
                      </div>

                      <div className="details">
                        <div className="details-col-1">
                          {_.map(exercise.sets, (it) => {
                            const equippedRepMax = locals.fromMetric(
                              it.equippedRepMax
                            );
                            const repMax = equippedRepMax || defaultRepMax;
                            const load = locals.fromMetric(it.load);
                            const percentRM = _.round((load / repMax) * 100, 1);
                            const isEquipped = !isAssistant && it.equipped;
                            const hasLoad = Boolean(load);
                            return (
                              <div className="routine">
                                <div>
                                  <b>Sets</b> {it.sets}
                                </div>
                                <div>
                                  <b>Reps</b> {it.reps}
                                </div>
                                {hasLoad && (
                                  <div>
                                    <b>Load</b> {locals.withWeight(load)}
                                  </div>
                                )}
                                {hasLoad && !isAssistant && (
                                  <div>
                                    <b>Intensity</b> {percentRM}%
                                  </div>
                                )}
                                {it.rpe > 0 && (
                                  <div>
                                    <b>RPE</b> {it.rpe}
                                  </div>
                                )}

                                {/*
																	NEEDS TO BE HOOED UP HERE AND ON THE DASHBOARD (daily/weekly) & EDIT EXERCISE
																	use the same zone key words that we use on the zone of relative intensity chart in the class
																 */}
                                <div>
                                  <b>Zone</b>{" "}
                                  <span
                                    className={`zone-dot ${it.zone}`}
                                  ></span>
                                </div>

                                {isEquipped && (
                                  <div>
                                    <b>Equipped</b> {it.equipment}
                                  </div>
                                )}

                                {isEquipped && (
                                  <div>
                                    <b>Equipped RM</b>{" "}
                                    {locals.withWeight(equippedRepMax)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="details-col-2">
                          {exercise.notes && (
                            <div className="notes">
                              <b>Notes</b> {exercise.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="foot">
                <a
                  className="button delete"
                  href={`/actions/exercise/remove-all?${_.map(
                    activeExercises,
                    ({ _id }, i) => `exercises[${i}]=${_id}`
                  ).join("&")}`}
                >
                  Delete Workout
                </a>

                <a
                  className="share-workout text-button icon"
                  href="/actions/refresh?modal[type]=share"
                >
                  <img src="/img/svg/icon_share.svg" alt="icon" />
                  Share Workout
                </a>
              </div>

              {query.expand ? (
                <a className="expand-all" href="/actions/refresh?expand=">
                  - Collapse All
                </a>
              ) : (
                <a className="expand-all" href="/actions/refresh?expand=true">
                  + Expand All
                </a>
              )}
            </div>
          </div>

          <div key={query.day} className="section Quick-Metrics">
            <h4 className="alt">
              Quick Metrics &nbsp;{" "}
              <span className="thin">{getDayRangeString(metricDayRange)}</span>
            </h4>
            <ToggleLinksInput
              name="period"
              options={[
                { text: "Weekly", value: "week" },
                { text: "Daily", value: "day" },
              ]}
            />

            <br />

            <div className="columns">
              <div className="column" data-col="1/3">
                <div
                  className="Chart-Frame bar-chart"
                  data-chart="total-volume"
                >
                  <div className="head">
                    <div className="right-side">
                      <div className="group">
                        <ToggleLinksInput
                          name="units"
                          options={[
                            { text: "kg", value: "metric" },
                            { text: "lbs", value: "imperial" },
                          ]}
                        />
                      </div>
                    </div>

                    <div className="left-side">
                      <div className="group">
                        <b>
                          Total Volume:{" "}
                          {locals.withWeight(_.round(totalVolume), units)}
                        </b>
                      </div>
                    </div>
                  </div>

                  <div className="body">
                    {!_.isEmpty(exercisesVolume) && (
                      <div>
                        <div className="legend">{PRIMARIES_LEGEND}</div>
                        <div className="chart">
                          <Chart
                            type="Bar"
                            data={{ series: seriesVolume }}
                            options={{
                              seriesBarDistance: 40,
                              axisX: { showGrid: false },
                              axisY: { onlyInteger: true },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="column" data-col="1/3">
                <div className="Chart-Frame custom-data">
                  <div className="head">
                    <div className="left-side">
                      <div className="group">
                        <b>Weighted Average Intensity</b>
                      </div>
                    </div>
                  </div>

                  <div className="body">
                    {!_.isEmpty(exercisesVolume) && (
                      <div className="chart">
                        <table>
                          <tbody>
                            {_.map(PRIMARIES, (name) => {
                              const average = _.get(
                                exercisesVolume,
                                [name, "average"],
                                0
                              );
                              const intensity = (average * 100).toFixed(1);
                              return (
                                <tr key={name}>
                                  <td>{_.capitalize(name)}</td>
                                  <td className={name}>{intensity}%</td>
                                </tr>
                              );
                            })}
                            <tr>
                              <td colSpan="2"></td>
                            </tr>
                            <tr>
                              <td>Total</td>
                              <td className="total">
                                {(totalIntensity * 100).toFixed(1)}%
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="column" data-col="1/3">
                <div className="Chart-Frame custom-data">
                  <div className="head">
                    <div className="left-side">
                      <div className="group">
                        <b>Number of Lifts</b>
                      </div>
                    </div>
                  </div>

                  <div className="body">
                    {!_.isEmpty(exercisesVolume) && (
                      <div className="chart">
                        <table>
                          <tbody>
                            {_.map(PRIMARIES, (name) => {
                              const lifts = _.get(exercisesLifts, name, 0);
                              return (
                                <tr key={name}>
                                  <td>{_.capitalize(name)}</td>
                                  <td className={name}>{lifts}</td>
                                </tr>
                              );
                            })}
                            <tr>
                              <td colSpan="2"></td>
                            </tr>
                            <tr>
                              <td>Total</td>
                              <td className="total">{totalLifts}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="column" data-col="1/2">
                <div className="Chart-Frame pie-chart">
                  <div className="head">
                    <div className="right-side">
                      <div className="group">
                        <ToggleLinksInput
                          className="tabs"
                          name="zoneType"
                          options={[
                            { text: "Total", value: "total" },
                            { text: "Squat", value: "squat" },
                            { text: "Bench", value: "bench" },
                            { text: "Deadlift", value: "deadlift" },
                          ]}
                        />
                      </div>
                    </div>

                    <div className="left-side">
                      <div className="group">
                        <b>Zone of Relative Intensity</b>
                      </div>
                    </div>
                  </div>

                  <div className="body">
                    {!_.isEmpty(exercisesVolume) && (
                      <div>
                        <div className="legend">{ZONES_LEGEND}</div>
                        <div className="chart" data-ratio="16:9">
                          <Chart
                            type="Pie"
                            data={{ series: seriesZones }}
                            options={{
                              labelInterpolationFnc: (val, i) => {
                                if (_.get(seriesZones, [i, "name"]) === "empty")
                                  return "";
                                return `${Math.round(val * 100)}%`;
                              },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="column" data-col="1/2">
                <div className="Chart-Frame pie-chart">
                  <div className="head">
                    <div className="left-side">
                      <div className="group">
                        <b>Volume Split by Core Lifts</b>
                      </div>
                    </div>
                  </div>

                  <div className="body">
                    {!_.isEmpty(exercisesVolume) && (
                      <div>
                        <div className="legend">{PRIMARIES_LEGEND}</div>
                        <div className="chart" data-ratio="16:9">
                          <Chart
                            type="Pie"
                            data={{ series: seriesVolumeSplit }}
                            options={{
                              labelInterpolationFnc: (val) =>
                                val &&
                                ((val / totalVolume) * 100).toFixed(0) + "%",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }
}

function handleDayChange() {
  submit("#_", { body: document.getElementById("program-builder-form") });
}

const ProgramBuilderV1 = (app) => {
  app.get("/v1", async ({ req, res, locals, load, api, fail }) => {
    const { query } = req;
    const { units } = locals;
    const user = locals.activeUser._id;
    const modal = _.get(query, "modal", {});

    _.defaults(query, {
      units,
      period: "day",
      day: moment(query.day).format(DATE_DAY_FORMAT),
      zoneType: "total",
    });

    if (query.day instanceof Date)
      query.day = moment(query.day).format(DATE_DAY_FORMAT);
    const startDate = moment(query.day, DATE_DAY_FORMAT).startOf("day");
    const endDate = moment(query.day, DATE_DAY_FORMAT).endOf("day");
    const metricsEndDate =
      query.period === "week" ? endDate.clone().add(6, "days") : endDate;

    await Promise.all([
      modal.date &&
        load("copyExercises", { date: moment(modal.date, "YYYY-MM") }),
      load("activeExercises", {
        start: startDate,
        end: endDate,
      }),
      load("exercisesVolume", {
        start: startDate,
        end: metricsEndDate,
      }),
      load("exercisesLifts", {
        start: startDate,
        end: metricsEndDate,
      }),
      load("exercisesVolumeGroupZones", {
        start: startDate,
        end: metricsEndDate,
      }),
    ]);

    if (modal.type === "share" && !modal.snapshot) {
      // create share link
      try {
        modal.snapshot = (
          await (
            await api("snapshot", {
              method: "POST",
              body: {
                type: "workout",
                data: JSON.stringify(
                  _.chain(locals)
                    .pick(["activeExercises"])
                    .assign({
                      start: startDate.toJSON(),
                      end: endDate.toJSON(),
                    })
                    .value()
                ),
              },
            })
          ).json()
        )._id;

        modal.date = getDayRangeString([startDate, endDate]);

        return res.refreshWith({ modal });
      } catch (e) {
        console.error(e.stack || e);
        fail(400, "Error creating share link.");
      }
    }

    locals.modal = {
      copy: <CopyExerciseModal />,
      export: <ExportWorkoutModal />,
      share: <ShareModal />,
    }[modal.type];

    res.body = <View />;
  });
};

export default ProgramBuilderV1;
